/**
 * FormOperatingModeContext
 * React context that provides the current form operating mode and its configuration
 * to all child controls. Eliminates prop-drilling of `mode` through the component tree.
 */

import React, { createContext, useContext, useMemo } from 'react';
import type { OperatingMode, OperatingModeConfig } from '@atlas-forms/types-js';
import { getModeConfig } from '../utils/modeConfig';

export interface ModeContext {
  mode: OperatingMode;
  config: OperatingModeConfig;
}

const FormOperatingModeContext = createContext<ModeContext>({
  mode: 'edit',
  config: getModeConfig('edit'),
});

export interface FormOperatingModeProviderProps {
  mode: OperatingMode;
  children: React.ReactNode;
}

export function FormOperatingModeProvider({
  mode,
  children,
}: FormOperatingModeProviderProps) {
  const modeContext = useMemo<ModeContext>(() => ({
    mode,
    config: getModeConfig(mode),
  }), [mode]);

  return (
    <FormOperatingModeContext.Provider value={modeContext}>
      {children}
    </FormOperatingModeContext.Provider>
  );
}

/**
 * Hook to read the full mode context (mode + config).
 * Use this when you need access to mode configuration rules.
 */
export function useModeContext(): ModeContext {
  return useContext(FormOperatingModeContext);
}

/**
 * Hook to read just the current form operating mode from context.
 * Defaults to 'edit' when used outside a provider.
 * Kept for backward compatibility.
 */
export function useFormOperatingMode(): OperatingMode {
  const context = useContext(FormOperatingModeContext);
  return context.mode;
}

export { FormOperatingModeContext };
